import { useEffect, useState, useRef } from 'react';
import { Stack, usePathname, useRouter, useNavigationContainerRef, router as globalRouter } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { Colors } from '../constants/theme';
import { View, Platform, StyleSheet, BackHandler, ToastAndroid, Linking } from 'react-native';
import { useAppStore } from '../store/useAppStore';
import { huellaDelDispositivo } from '../services/identidadDeDispositivo';
import { callRegistrarDispositivo, callSincronizarMiCuenta } from '../services/cloudFunctions';
import { useFonts } from 'expo-font';
import { Ionicons } from '@expo/vector-icons';
import { getNativeAuth, observarSesion } from '../services/firebaseConfig';
import { onAuthStateChanged as nativeOnAuthStateChanged } from '@react-native-firebase/auth';
import { firestoreService } from '../services/firestoreService';
import {
  restAuthCache,
  getCurrentBridgeUser,
  authBridge,
  sesionCerrandose,
  generacionDeSesion,
} from '../services/authBridge';
import { rideSessionStore } from '../services/rideSessionStore';
import {
  AVISOS_SIN_NAVEGACION,
  destinoDeAvisoDeComida,
  esAvisoDeComida,
  type DestinoDeAviso,
} from '../services/rutaDeAviso';
import { StripeProviderWrapper } from '../components/StripeProviderWrapper';
import OfflineBanner from '../components/OfflineBanner';
// SIN ESTO, UN ARREGLO PUBLICADO PUEDE NO LLEGAR NUNCA. El binario baja la
// actualizacion por detras y la aplica en el siguiente ARRANQUE EN FRIO, que en
// Android puede tardar dias: volver desde las recientes NO reinicia el proceso.
// `expo-updates` estaba instalado y ninguna linea de la app lo usaba.
import AvisoDeActualizacion from '../components/AvisoDeActualizacion';
import { ErrorBoundary, instalarManejadorGlobal } from '../components/ErrorBoundary';
import { errorReporter } from '../services/errorReporter';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { notificationService } from '../services/notificationService';
import DesktopSidebarLayout from '../components/DesktopSidebarLayout';
import { LOGIN_ENFORCED, KYC_WALL_ENFORCED, VERTICAL_ESCOLAR_VISIBLE } from '../constants/featureFlags';
import { configService } from '../services/configService';
import { useAppStateGuard } from '../hooks/useAppStateGuard';

// Rutas desde las que SÍ debemos redirigir post-login con guard KYC/email.
// Estas son las pantallas de "entrada" — si el usuario ya está adentro del
// app navegando, no lo movemos. Incluimos /verify-email para que si el
// usuario reabre la app desde esa pantalla habiendo ya verificado en web,
// el observer lo mande automáticamente al KYC.
const POST_LOGIN_ENTRY_ROUTES = new Set<string>([
  '/', '/auth', '/verify-code', '/verify-email', '/role-selection', '/onboarding',
]);

// ══════════════════════════════════════════════════════════════════════════
// "AHORA NO" — la verificación se recuerda, no se impone.
//
// QUÉ ESTABA PASANDO: el guardián de más abajo mandaba a /kyc-welcome a todo
// usuario sin verificar CADA vez que pasaba por una ruta de entrada (arranque
// en frío, login, selector de rol). El embudo no tenía salida, así que un
// pasajero recién registrado —y el revisor de Google Play— no llegaba nunca al
// mapa: se registraba, caía en la pantalla de la cédula y ahí se acababa la
// app. Es literalmente el rechazo que ya recibimos ("la app no abre o no
// carga"). Y lo hacía con `KYC_WALL_ENFORCED = false`, es decir, con la
// bandera que existe justo para NO obligar a nadie.
//
// CÓMO QUEDA: con la bandera apagada, la verificación es un RECORDATORIO que
// se enseña UNA SOLA VEZ por sesión y del que se sale con "Ahora no". Se
// guarda en memoria y no en disco a propósito: al abrir la app otro día se le
// vuelve a recordar una vez, sin llegar nunca a ser un muro.
//
// LO QUE SIGUE BLOQUEADO: el CHOFER necesita estar verificado para ponerse en
// línea y aceptar viajes. Eso lo exigen las reglas de Firestore
// (`isVerifiedDriver()` para ofertar y aceptar) y el gate de map.tsx — vive en
// el servidor y aquí no se toca. Lo que se abre es el camino del PASAJERO.
let recordatorioKycYaResuelto = false;

/** Lo llama /kyc-welcome cuando el usuario toca "Ahora no" o "Cambiar de rol". */
export function aplazarVerificacionEnEstaSesion(): void {
  recordatorioKycYaResuelto = true;
}

/** true si en esta sesión ya se le recordó la verificación (o él la aplazó). */
export function verificacionAplazadaEnEstaSesion(): boolean {
  return recordatorioKycYaResuelto;
}

/**
 * A dónde va cada rol cuando ya no hay nada pendiente que pedirle.
 *
 * Vive aquí —y lo importa /kyc-welcome— para que el botón "Ahora no" mande
 * exactamente al mismo sitio que el guardián. Con dos tablas separadas, una
 * de las dos se queda vieja el día que se añada un rol.
 */
export function rutaSegunRol(role: string | null | undefined): string {
  switch (role) {
    case 'RENT_CAR_OWNER': return '/(tabs)/dashboard';
    case 'CAR_RENTER': return '/(tabs)/marketplace';
    // ⚠️ MIENTRAS `VERTICAL_ESCOLAR_VISIBLE` ESTÉ APAGADA, AQUÍ NO.
    // El panel del director se auto-expulsa cuando la vertical escolar está
    // oculta (useGuardiaEscolar devuelve true y la pantalla hace replace a
    // '/'), y esta tabla lo mandaba de vuelta: '/' → panel → '/' → panel…
    // Una cuenta con este rol —y el rol se restaura solo desde Firestore al
    // reinstalar— arrancaba en pantalla blanca, que es exactamente como se ve
    // «la app no carga» en una revisión de Google. Al mapa, como cualquier rol
    // sin panel propio; el día que la vertical se encienda, se devuelve esta
    // línea a su sitio.
    case 'SCHOOL_DIRECTOR':
      return VERTICAL_ESCOLAR_VISIBLE ? '/school-director-dashboard' : '/(tabs)/map';
    case 'RESTAURANT_OWNER': return '/restaurant-dashboard';
    case 'FOOD_DELIVERY': return '/(tabs)/food';
    // PASSENGER, DRIVER y cualquier rol sin panel propio → el mapa.
    default: return '/(tabs)/map';
  }
}

// Centinela para el timeout de la lectura del perfil. Ver el uso más abajo:
// "no pude leer" NO es lo mismo que "no está verificado".
const PERFIL_ILEGIBLE = Symbol('perfil-ilegible');

// Rutas en las que el owner web debe ver el sidebar persistente.
// expo-router elimina los grupos `(tabs)` del pathname, así que aquí
// listamos los pathnames REALES tal cual los reporta usePathname().
// Excluye explícitamente /partner, /auth y / (índice) para que el login
// no aparezca dentro del shell.
const OWNER_PANEL_ROUTES = new Set<string>([
  '/dashboard',
  '/fleet',
  '/drivers',
  '/rent-chats',
  '/marketplace',
  '/wallet',
  '/notifications',
  '/crm-settings',
  '/edit-profile',
  '/rent-car-chat',
  '/driver-bank-accounts',
  '/payment-methods',
]);

// Rutas adicionales permitidas en web prod además del panel: login y utilidades.
// Cualquier pathname FUERA de estas listas se redirige a /partner.
const PUBLIC_WEB_ROUTES = new Set<string>([
  '/',          // index — redirige a /partner internamente
  '/partner',   // login owner
  '/+not-found',
  '/_sitemap',
  // La política y los términos TIENEN que abrirse sin sesión: Play Console
  // exige la URL de la política en la ficha y el revisor la abre en frío.
  // Estaban cayendo en el login del panel de Rent Car, así que esa URL no
  // mostraba ninguna política — se marca como política ausente o inaccesible,
  // que es incumplimiento directo.
  '/privacy',
  '/terms',
  // ══════════════════════════════════════════════════════════════════════
  // EL ENLACE MÁGICO DEL RENT CAR TIENE QUE ABRIRSE SIN SESIÓN Y SIN APP
  // ══════════════════════════════════════════════════════════════════════
  //
  // `/onboard/{uid}` es la puerta por la que el dueño de un rent car mete a un
  // chofer candidato: le manda el enlace por WhatsApp, el candidato lo abre,
  // llena sus datos y aparece en el panel del dueño. Todo el sentido del
  // enlace es que funcione SIN que el candidato tenga la app ni una cuenta.
  //
  // Estaba cerrado POR DOS SITIOS A LA VEZ, los dos por arrastre:
  //   1. `firebase.json` lo redirigía con un 302 a /partner junto con todas
  //      las demás rutas de la app (quitado hoy).
  //   2. Y aquí: al no estar en ninguna de las dos listas, el guardián de web
  //      lo mandaba otra vez a /partner.
  //
  // /partner es el login EXCLUSIVO del dueño, y encima echa a quien no lo es.
  // O sea que el candidato abría el enlace y leía que ese portal no es para
  // él. El puente estaba cortado en el primer paso, y lo que fallaba no era
  // el formulario —que funciona— sino la puerta.
  //
  // Es el mismo caso que /privacy y /terms de aquí arriba: una URL que un
  // desconocido abre en frío, sin sesión.
  '/onboard',
]);

function isLocalhostHost(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.location?.hostname &&
    window.location.hostname.includes('localhost')
  );
}

/**
 * Rutas públicas que llevan algo detrás de la barra.
 *
 * ⚠️ `PUBLIC_WEB_ROUTES` se comprueba con `.has(pathname)`, o sea MATCH EXACTO.
 * El bucle de prefijos de abajo solo recorre las del panel del dueño. Así que
 * meter '/onboard' en la lista pública NO abre '/onboard/{uid}', que es la
 * forma que tiene el enlace de verdad — y eso es lo que pasó: se añadió, se
 * desplegó, y en producción el enlace seguía rebotando a /partner. Se vio
 * abriéndolo, no leyéndolo.
 */
// `/mi-postulacion` va aqui por la misma razon que `/onboard`: el candidato
// llega por el enlace magico, EN EL NAVEGADOR y sin app, y al enviar sus
// papeles aterriza ahi. Si no estuviera, el guardian lo mandaria a /partner
// justo despues de mandar sus documentos.
const PUBLIC_WEB_PREFIXES = ['/onboard', '/mi-postulacion'];

function isRoutePermittedInWebProd(pathname: string | null): boolean {
  if (!pathname) return false;
  if (PUBLIC_WEB_ROUTES.has(pathname)) return true;
  for (const r of PUBLIC_WEB_PREFIXES) {
    if (pathname === r || pathname.startsWith(r + '/')) return true;
  }
  for (const r of OWNER_PANEL_ROUTES) {
    if (pathname === r || pathname.startsWith(r + '/')) return true;
  }
  return false;
}

function shouldUseOwnerShell(pathname: string | null, role: string | null | undefined): boolean {
  if (Platform.OS !== 'web') return false;
  if (role !== 'RENT_CAR_OWNER') return false;
  if (!pathname) return false;
  // Match exacto o prefijo (para rutas anidadas)
  for (const r of OWNER_PANEL_ROUTES) {
    if (pathname === r || pathname.startsWith(r + '/')) return true;
  }
  return false;
}

// ══════════════════════════════════════════════════════════════════════════
// MAPA DE ESPERAS DEL ARRANQUE — cada una con su reloj y su salida.
//
// El patrón de fallo de esta app siempre ha sido el mismo: se espera un evento
// que puede no llegar y no hay plazo. Cualquier espera nueva en el arranque va
// en esta lista, con el número escrito, o no va.
//
//   · FUENTES (ESPERA_FUENTES_MS = 2 s) — `useFonts` puede no resolver nunca
//     (verificado en el export web: `loaded=false` y `error=null` para
//     siempre). A los 2 s se destapan a la vez el dibujado Y el splash, y los
//     íconos caen al tipo del sistema. Cosmético; jamás bloquea.
//
//   · LECTURA DEL PERFIL (8 s, más abajo, `PERFIL_ILEGIBLE`) — Firestore sobre
//     Hermes se cuelga. Al vencer NO se decide nada: "no pude leer" no es "no
//     está verificado", así que no se enruta ni se apagan banderas.
//
//   · HIDRATACIÓN DEL DISCO — no vive aquí. Bloquea el dibujado en
//     `(tabs)/_layout.tsx` y allí tiene su propio plazo de 3 s.
//
// Lo demás del arranque son avisos sueltos que NO bloquean el dibujado: el
// observador de sesión, `restAuthCache.hydrate()`, la captura del referido por
// enlace, el precalentado de configuración y el enrutado por notificación. Si
// alguno no resuelve se pierde esa función concreta; la app arranca igual.
// ══════════════════════════════════════════════════════════════════════════
const ESPERA_FUENTES_MS = 2000;

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

if (Platform.OS === 'web' && typeof document !== 'undefined') {
  // AUDIT FIX BAJO — Envuelto en try/catch: algunos navegadores con CSP
  // estricta (`style-src 'none'`) bloquean `document.createElement('style')`
  // y la app entera crasheaba al boot solo por la inyección de la fuente
  // de íconos. Ahora si el browser lo rechaza, los íconos quedan sin la
  // fuente custom (caen al fallback del sistema) pero la app sigue viva.
  try {
    const IoniconsFont = require('@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/Ionicons.ttf');
    const iconFontStyles = `@font-face {
      src: url(${IoniconsFont});
      font-family: Ionicons;
    }`;
    const style = document.createElement('style');
    style.type = 'text/css';
    style.appendChild(document.createTextNode(iconFontStyles));
    document.head.appendChild(style);
  } catch (e) {
    console.warn('[_layout] No se pudo inyectar la fuente Ionicons (CSP?):', e);
  }
}

export default function RootLayout() {
  const pathname = usePathname();
  const router = useRouter();
  // Solo se LEE en el toque de un push (no suscribe a la navegación).
  const navRef = useNavigationContainerRef();
  // Selectores específicos: evita re-renderizar el árbol entero ante cualquier cambio del store
  const userRole = useAppStore((state) => state.userRole);
  const hasHydrated = useAppStore((state) => state._hasHydrated);
  const setUserFirebaseUid = useAppStore((state) => state.setUserFirebaseUid);
  const setProfile = useAppStore((state) => state.setProfile);
  const setVerified = useAppStore((state) => state.setVerified);
  const setDriverVerified = useAppStore((state) => state.setDriverVerified);

  // Único sitio de toda la app donde se escucha si pasamos a segundo plano.
  // Va en la raíz porque el vigilante de GPS es estado del PROCESO, no de una
  // pantalla: montarlo dentro del mapa lo dejaría sordo en cuanto el chofer
  // navegara a otra pestaña, que es justo cuando minimiza. Ver el comentario
  // largo de hooks/useAppStateGuard.ts para el ciclo minimizar→volver.
  useAppStateGuard();

  const [loaded, error] = useFonts({
    ...Ionicons.font,
  });

  // Salvavidas anti-pantalla-en-blanco: si la carga de fuentes no resuelve
  // (pasa en el export web), arrancamos igual pasados 2 s. Ver la nota del
  // guard `if (!loaded && !error && !fontTimeout)` más abajo.
  const [fontTimeout, setFontTimeout] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFontTimeout(true), ESPERA_FUENTES_MS);
    return () => clearTimeout(t);
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  // EL SPLASH TAMBIÉN SE VA CON EL SALVAVIDAS.
  //
  // Este efecto dependía solo de `[loaded, error]`. El salvavidas de arriba
  // existía y desbloqueaba el DIBUJADO, pero no estaba aquí ni en estas
  // dependencias: si `useFonts` no resolvía nunca —ni con éxito ni con
  // error— la app quedaba viva y funcionando DEBAJO de un splash clavado.
  // Desde fuera eso no se distingue de un cuelgue: es exactamente lo que se
  // reportó como "se congela". La app respondía; nadie podía verla.
  //
  // Ahora la condición es la MISMA que la del guard del render, así que el
  // splash y el contenido se destapan a la vez y no puede haber uno sin otro.
  // El ref evita llamar dos veces cuando `loaded` llega después del plazo.
  const splashYaOculto = useRef(false);
  useEffect(() => {
    if (!loaded && !error && !fontTimeout) return;
    if (splashYaOculto.current) return;
    splashYaOculto.current = true;
    // `.catch` porque en web (o si el splash ya se fue) esto rechaza, y una
    // promesa rechazada aquí sería otra vez un arranque roto en silencio.
    SplashScreen.hideAsync().catch(() => {});
  }, [loaded, error, fontTimeout]);

  // ──────────────────────────────────────────────────────────────────────
  // AUTH OBSERVER + ENRUTADO POST-LOGIN
  // ----------------------------------------------------------------------
  // Cuando Firebase Auth confirma un user logueado, consultamos /users/{uid}
  // en Firestore, leemos `kycCompleted` (o `isVerified` como compatibilidad
  // legacy) y `isDriverVerified`, y enrutamos al panel de su rol.
  //   - verificado          → su pantalla (mapa, comida, dashboard…)
  //   - sin verificar       → UNA visita a /kyc-welcome por sesión, como
  //                           recordatorio con salida ("Ahora no"); después,
  //                           su pantalla igual. Ver la nota de
  //                           `recordatorioKycYaResuelto` arriba del archivo.
  //   - con KYC_WALL_ENFORCED=true → sí se le exige antes de entrar.
  // Operar de verdad (ponerse en línea, aceptar viajes, ofertar) lo siguen
  // guardando las reglas de Firestore, no esta pantalla.
  //
  // La redirección solo dispara desde rutas de "entrada" (/, /auth, etc.).
  // Si el user ya está adentro navegando, no lo movemos.
  // ──────────────────────────────────────────────────────────────────────
  // Hidrata el cache REST al arrancar — si en el run anterior el signup
  // cayó al fallback REST y se persistió en AsyncStorage, levantamos ese
  // user para que getCurrentBridgeUser() lo vea desde el primer momento.
  useEffect(() => {
    restAuthCache.hydrate();
  }, []);

  // AUDIT FIX M-3 — Separamos el observer de auth (deps: solo setters) del
  // routing reactivo (deps: pathname). Antes ambos vivían en el mismo
  // useEffect con pathname en deps, lo cual causaba unsubscribe + re-subscribe
  // de onAuthStateChanged en CADA cambio de pantalla. Cada re-subscribe
  // disparaba el callback inmediatamente, que a su vez disparaba una query
  // Firestore que puede colgarse hasta 8s (Web SDK + Hermes). Resultado:
  // navegación con latencia innecesaria y carga de Firestore inútil.
  //
  // Ahora:
  //   useEffect #1 (este): suscribe UNA sola vez al auth observer, solo
  //     mantiene el store actualizado (uid, profile, push notifications).
  //     NO toca router. NO depende de pathname.
  //   useEffect #2 (abajo): reacciona a cambios de pathname Y al uid del
  //     store, ejecuta el routing (email gate, KYC gate, redirects).
  useEffect(() => {
    let alive = true;
    // `observarSesion` y no `nativeOnAuthStateChanged(getNativeAuth(), …)`:
    // en web el SDK nativo no tiene app inicializada y esto reventaba aquí
    // mismo, al montar el layout raíz, dejando la web en blanco.
    const unsubscribe = observarSesion((user) => {
      if (!alive) return;

      // H32, auditoría 16-sep-2026 — Mientras `authBridge.signOut()` corre, lo
      // que diga el puente es la sesión que se está cerrando. Leerla aquí
      // devolvía el uid al store, re-registraba el token de push recién
      // borrado y hasta disparaba otro canje de sesión nativa.
      if (sesionCerrandose()) {
        setUserFirebaseUid(null);
        return;
      }

      const bridge = getCurrentBridgeUser();
      const effectiveUid = user?.uid ?? bridge?.uid ?? null;
      const effectiveEmail = user?.email ?? bridge?.email ?? null;

      if (!effectiveUid) {
        setUserFirebaseUid(null);
        return;
      }
      setUserFirebaseUid(effectiveUid);
      // Hay sesión real → se acabó la vista previa. Quien ya tiene cuenta no
      // debe seguir viendo los avisos ni los bloqueos de invitado.
      // Solo se apaga al DETECTAR sesión: perderla no vuelve a encenderla,
      // porque salir de la cuenta debe llevar al selector normal, no a un
      // modo invitado que el usuario nunca pidió.
      useAppStore.getState().setGuestPreview(false);
      // FIX BUG #2 (causa raíz #1) — RECUPERACIÓN de sesión nativa en el
      // arranque: si el SDK nativo NO tiene user (`user` es null) pero SÍ hay
      // sesión (bridge/REST → effectiveUid), disparamos el exchange para poblar
      // getNativeAuth().currentUser. Sin esto, un usuario que quedó en sesión
      // REST (Google, o email cuyo exchange falló) arrancaba SIN request.auth
      // nativo → toda operación de viaje era rechazada en silencio. El comment
      // de authBridge prometía "se reintenta en próximas operaciones" pero no
      // existía. Fire-and-forget con retry interno; NO hace loop (al entrar el
      // custom token, el observer re-dispara con user!=null y esta rama no corre).
      if (!user) {
        authBridge.getIdToken()
          .then((t) => { if (t) authBridge.ensureNativeSession(t); })
          .catch(() => {});
      }
      if (effectiveEmail || user?.phoneNumber) {
        // FIX BUG #1 (causa raíz #2) — Antes el teléfono se hardcodeaba a ''
        // aquí: `setProfile(currentName, user?.phoneNumber || '', ...)`. Como
        // el proyecto NO usa login por SMS, `user.phoneNumber` SIEMPRE es null
        // → escribía userPhone='' en CADA arranque, BORRANDO en disco el
        // teléfono guardado en signup/add-phone → los gates re-pedían el
        // teléfono cada vez que se abría la app. El NOMBRE ya se preservaba
        // leyéndolo del store (currentName); el teléfono NO. Ahora es simétrico:
        // preservamos el userPhone existente del store.
        const currentName = useAppStore.getState().userName;
        const currentPhone = useAppStore.getState().userPhone;
        setProfile(currentName, user?.phoneNumber || currentPhone || '', effectiveEmail || '');
      }
      notificationService.registerForPushNotifications(effectiveUid).catch(() => {});
      // Canjear un código de referido pendiente (deep link o campo de registro).
      // Se hace aquí — con la sesión ya confirmada — y no justo tras el signup,
      // porque la sesión nativa puede tardar unos ms en establecerse. Nunca
      // rompe el arranque: applyPendingIfAny traga sus propios errores.
      import('../services/referralService')
        .then(({ referralService }) => {
          referralService.applyPendingIfAny().catch(() => {});
          // Y se CREA el código propio desde el primer arranque. Antes solo
          // nacía al pulsar "Invitar Amigos", así que `referral_codes` estaba
          // vacía en producción y pasaban dos cosas: quien intentaba canjear el
          // código de alguien que nunca pulsó compartir recibía "ese código no
          // existe", y el propio dueño no podía dictarlo por WhatsApp porque la
          // app no se lo enseñaba en ningún sitio. Es idempotente y barato: si
          // ya existe, devuelve el mismo. Nunca rompe el arranque.
          referralService.getMyCode().catch(() => {});
        })
        .catch(() => {});
    });
    return () => { alive = false; unsubscribe(); };
  }, [setUserFirebaseUid, setProfile]);

  // CAPTURA DE REFERIDO POR DEEP LINK — el invitado abre un enlace del tipo
  // `https://gosafely-3e0e7.web.app/?ref=CODE` o `gosafelyapp://?ref=CODE`.
  // Guardamos el código; se canjea cuando la sesión esté confirmada (arriba,
  // applyPendingIfAny). Extraemos ?ref= de cualquier URL sin interferir con el
  // routing de expo-router. Soporta también la ruta `/r/CODE`.
  useEffect(() => {
    const extractCode = (url: string | null): string | null => {
      if (!url) return null;
      const q = url.match(/[?&]ref=([A-Za-z0-9]+)/);
      if (q?.[1]) return q[1];
      const p = url.match(/\/r\/([A-Za-z0-9]+)/);
      if (p?.[1]) return p[1];
      return null;
    };
    const handle = (url: string | null) => {
      const code = extractCode(url);
      if (!code) return;
      import('../services/referralService')
        .then(({ referralService }) => referralService.setPending(code))
        .catch(() => {});
    };
    Linking.getInitialURL().then(handle).catch(() => {});
    const sub = Linking.addEventListener('url', (e) => handle(e.url));
    return () => { try { sub.remove(); } catch {} };
  }, []);

  // Routing reactivo: corre cuando cambia pathname, uid del store o el
  // bridge. Hace los redirects de email/KYC. NO mantiene listener nativo,
  // así que cambiar de pantalla NO causa re-subscribe.
  const userFirebaseUidFromStore = useAppStore((state) => state.userFirebaseUid);

  // ══════════════════════════════════════════════════════════════════════
  // ATAR ESTA CUENTA AL TELÉFONO DESDE EL QUE ENTRÓ
  // ══════════════════════════════════════════════════════════════════════
  //
  // ── POR QUÉ ───────────────────────────────────────────────────────────
  //
  // El candado que impide cobrar dos veces el viaje gratis conoce tres llaves:
  // teléfono verificado, correo verificado y cédula. A quien entra con Google
  // le queda SOLO el correo — y un Gmail nuevo llega con `emailVerified: true`
  // de fábrica. O sea que la única llave de la mayoría cuesta dos minutos y
  // cero pesos fabricar. Con esta se añade la cuarta, que es la que sobrevive a
  // borrar la app y volver a instalarla.
  //
  // ── POR QUÉ AQUÍ Y NO EN LA PANTALLA DE LOGIN ────────────────────────
  //
  // Porque hay varias puertas de entrada (Google, correo, teléfono, y la
  // sesión que ya venía guardada de la vez anterior) y meterlo en cada una
  // sería garantizar que se olvida en alguna. Esto cuelga del uid: en cuanto
  // hay sesión, sea de donde sea, se manda.
  //
  // ── Y POR QUÉ NO SE LE DICE NADA A LA PERSONA SI FALLA ───────────────
  //
  // Porque no es nada que ella pueda arreglar, y porque un aviso aquí solo
  // serviría para enseñarle al que busca el truco que existe este candado. Si
  // falla, se reintenta en el siguiente arranque; y mientras tanto el servidor
  // trata ese aparato como desconocido, que es lo mismo que pasaba antes.
  const dispositivoYaMandado = useRef<string>('');
  useEffect(() => {
    const uid = userFirebaseUidFromStore;
    if (!uid || dispositivoYaMandado.current === uid) return;
    dispositivoYaMandado.current = uid;
    let vivo = true;
    (async () => {
      try {
        const huella = await huellaDelDispositivo();
        if (!vivo) return;
        // En iOS y en web no hay huella que valga, y `huellaDelDispositivo`
        // devuelve null a propósito en vez de inventarse una. Mandar algo
        // falso sería peor que no mandar nada: ataría la cuenta a un aparato
        // que no existe. Pero la sincronización de abajo SÍ corre igual: el
        // aparato es solo una de las cuatro llaves de identidad.
        if (huella) {
          await callRegistrarDispositivo({ huella });
          if (!vivo) return;
        }

        // ══════════════════════════════════════════════════════════════════
        // Y DESPUÉS, LO QUE LA PERSONA ARRASTRA DE OTRAS CUENTAS SUYAS
        // ══════════════════════════════════════════════════════════════════
        //
        // DESPUÉS y no antes: el servidor cuenta los viajes de cortesía por
        // llave de identidad, y el aparato es una de ellas. Si esto corriera
        // primero, la cuenta nueva abierta en un teléfono ya agotado saldría
        // limpia justo en el arranque que importa.
        //
        // Pone al día dos cosas: el tope de los 5 viajes sin verificarse, y
        // la multa de cancelaciones y el bloqueo de las 5 que esa persona
        // dejara en otra cuenta ya borrada. Esas dos últimas no necesitan
        // nada de pantalla aquí: `PenaltyGate` escucha el documento en vivo
        // y aparece solo, con su explicación y su botón de pagar.
        //
        // Va en una llamada aparte y SIN App Check a propósito: no manda ni
        // un dato del teléfono, y tiene que correr también en iOS y en web,
        // donde `registrarDispositivo` no se llama nunca. Ver la nota larga
        // en functions/src/sincronizar.ts.
        const r = await callSincronizarMiCuenta({});
        const veredicto = r?.data;
        if (vivo && veredicto?.requiereVerificacion === true) {
          // Solo se SUBE la bandera (ver `subirRequiereVerificacion`): la
          // lectura del perfil corre a la vez y traería el campo viejo.
          useAppStore.getState().subirRequiereVerificacion({
            hechos: Number(veredicto.viajesSinVerificar ?? 0) || 0,
            tope: Number(veredicto.viajesSinVerificarTope ?? 5) || 5,
          });
        }
        if (veredicto?.multaHeredada || veredicto?.bloqueoHeredado) {
          console.warn(
            '[sincronizar] esta cuenta heredó una sanción de otra anterior:',
            veredicto.multaHeredada,
            veredicto.bloqueoHeredado
          );
        }
      } catch (e: any) {
        // Silencio deliberado — ver la nota de arriba. Queda en el log para
        // que se pueda mirar si alguna vez hace falta.
        console.warn('[dispositivo] no se pudo poner al día la cuenta:', e?.message ?? e);
        // Se suelta la marca para que el siguiente arranque lo reintente.
        if (vivo) dispositivoYaMandado.current = '';
      }
    })();
    return () => {
      vivo = false;
    };
  }, [userFirebaseUidFromStore]);
  useEffect(() => {
    let alive = true;
    const run = async () => {
      // H32 — Durante el cierre de sesión el store ya está vacío pero el SDK
      // nativo todavía tiene al usuario (se cierra el último, a propósito). Sin
      // este corte, el reseteo del rol mandaba al usuario a "/" (ruta de
      // entrada), este guardián leía el perfil del que SE ESTÁ YENDO y le
      // volvía a escribir rol, nombre y foto al store recién limpiado.
      if (sesionCerrandose()) return;
      const generacion = generacionDeSesion();
      const bridge = getCurrentBridgeUser();
      const effectiveUid = userFirebaseUidFromStore ?? bridge?.uid ?? null;
      const effectiveEmail = bridge?.email ?? null;
      const effectiveVerified = bridge?.emailVerified ?? false;
      if (!effectiveUid) return;

      const currentPath = pathname;
      const isEntryRoute = currentPath && POST_LOGIN_ENTRY_ROUTES.has(currentPath);
      if (Platform.OS === 'web' || !isEntryRoute) return;

      if (effectiveEmail && !effectiveVerified) {
        if (currentPath !== '/verify-email') router.replace('/verify-email');
        return;
      }

      try {
        const profileQuery = firestoreService.getUserProfile(effectiveUid);
        // CENTINELA — antes el timeout resolvía `null`, exactamente el mismo
        // valor que "este usuario no tiene perfil". Ocho segundos de red lenta
        // se leían entonces como "no está verificado" y el usuario acababa en
        // el embudo de KYC aunque tuviera su escudo desde hacía meses. Un dato
        // que no se pudo leer no se rellena con uno plausible: se marca como
        // ilegible y no se decide nada con él.
        const timeoutQuery = new Promise<typeof PERFIL_ILEGIBLE>((resolve) =>
          setTimeout(() => resolve(PERFIL_ILEGIBLE), 8000)
        );
        const lectura: any = await Promise.race([profileQuery, timeoutQuery]);
        if (!alive) return;
        // La lectura tarda hasta 8 s: si en ese rato se cerró la sesión, este
        // perfil es de alguien que ya no está. No se escribe nada con él.
        if (sesionCerrandose() || generacion !== generacionDeSesion()) return;
        const perfilIlegible = lectura === PERFIL_ILEGIBLE;
        const profile: any = perfilIlegible ? null : lectura;
        const kycOk = !!(profile?.kycCompleted ?? profile?.isVerified);
        const driverKycOk = !!profile?.isDriverVerified;
        // Solo escribimos en el store lo que de verdad leímos. Si la lectura se
        // cayó, dejamos los flags persistidos como estaban en vez de apagarlos.
        if (!perfilIlegible) {
          setVerified(kycOk);
          setDriverVerified(driverKycOk);
          // LOS 5 VIAJES DE CORTESÍA. Se lee del MISMO perfil que ya se trajo:
          // sin esto, el aviso de map.tsx nunca saltaría y el sexto viaje
          // devolvería un `permission-denied` mudo. Quien ya está verificado
          // no tiene tope, así que se fuerza a false pase lo que pase en el
          // documento — el campo lo limpia `reviewKycSubmission` al aprobar,
          // pero esto cubre el rato entre la aprobación y la próxima lectura.
          useAppStore.getState().setEstadoDeVerificacion({
            requiere: !kycOk && profile?.requiereVerificacion === true,
            hechos: Number(profile?.viajesSinVerificar ?? 0) || 0,
            tope: Number(profile?.viajesSinVerificarTope ?? 5) || 5,
          });
        }

        // FIX RESTAURACIÓN DE ROL — Después de reinstalar la app, el store
        // local pierde userRole/baseRole. Si solo confiamos en lo que el
        // store dice, queda null → el user cae al picker general donde
        // "todos los perfiles están abiertos" en vez de ir a su dashboard
        // real. Ahora restauramos el rol desde Firestore (fuente de verdad)
        // antes de decidir el routing.
        //
        // FIX INFERENCIA — Cuentas creadas antes del fix de persistencia
        // no tienen profile.role en Firestore. Para evitar dejarlas
        // atrapadas, inferimos el rol de otros campos (isDriverVerified,
        // vehicleType, schoolName, etc.) y rotuemos. La persistencia
        // permanente del rol inferido la hace auth.tsx al login para no
        // duplicar la llamada al backend desde acá.
        const storeRoleBefore = useAppStore.getState().userRole;
        let persistedRole: string | null = profile?.role ?? null;
        let persistedBaseRole: string | null = profile?.baseRole ?? persistedRole;
        if (!persistedRole && profile) {
          // ══ AQUÍ SE INFERÍA «DIRECTORA» DE UN CAMPO DE TEXTO ══════════
          //
          // Había una rama que decía: si el perfil tiene `schoolName`, esta
          // cuenta es SCHOOL_DIRECTOR. Con el perfil escolar anulado
          // (5-oct-2026) eso era una trampa: cualquier cuenta que hubiera
          // pasado por el alta de la directora —una pantalla que estaba sin
          // candado y alcanzable sin sesión— renacía como directora EN CADA
          // ARRANQUE, aunque la tarjeta ya no exista, y aterrizaba en un panel
          // que se auto-expulsa: pantalla en blanco y de vuelta a la portada.
          //
          // Sin esa rama, esas cuentas caen en `kycOk → PASSENGER`, que es la
          // salida buena: entran y usan la app.
          //
          // Lo que SÍ se queda es la inferencia de SCHOOL_DRIVER por el
          // vehículo, porque justo debajo hay una auto-reparación que corrige
          // ese caso a DRIVER por el servidor. Esa sí termina bien.
          if (profile.vehicleType === 'SCHOOL_BUS') {
            persistedRole = 'SCHOOL_DRIVER';
          } else if (profile.isDriverVerified === true || profile.vehicleType) {
            persistedRole = 'DRIVER';
          } else if (profile.restaurantOwnerOf || profile.restaurantId) {
            persistedRole = 'RESTAURANT_OWNER';
          } else if (kycOk) {
            persistedRole = 'PASSENGER';
          }
          persistedBaseRole = persistedRole;
        }
        // ══════════════════════════════════════════════════════════════════
        // AUTO-REPARACIÓN DE ROL INCOHERENTE.
        //
        // Un "chofer escolar" que no conduce un bus escolar no existe. Esa
        // combinación solo pudo quedar grabada por el fallo que arreglamos:
        // elegir carro/motor/minibús no guardaba el rol (se iba por Railway,
        // que está caído) y quedaba el rol anterior. El fundador lo vivió con
        // su propia cuenta: su vehículo es MINIBÚS y la app lo mandaba una y
        // otra vez al panel de transporte escolar.
        //
        // Corregir el código evita que vuelva a pasar, pero NO limpia lo que
        // ya está guardado. Esto sí: al entrar, si el rol y el vehículo no
        // concuerdan, se corrige solo por el servidor (setDriverRole valida y
        // escribe con Admin SDK) y el usuario ya entra a donde le toca.
        // ══════════════════════════════════════════════════════════════════
        const vt = profile?.vehicleType;
        if (persistedRole === 'SCHOOL_DRIVER' && vt && vt !== 'SCHOOL_BUS') {
          console.warn(`[_layout] rol incoherente: SCHOOL_DRIVER con vehículo ${vt} → corrigiendo a DRIVER`);
          persistedRole = 'DRIVER';
          persistedBaseRole = 'DRIVER';
          try {
            const { callSetDriverRole } = await import('../services/cloudFunctions');
            await callSetDriverRole({ role: 'DRIVER', vehicleType: vt });
          } catch (e: any) {
            // Si el servidor no responde, al menos la sesión actual queda bien.
            console.warn('[_layout] no se pudo persistir la corrección de rol:', e?.message);
          }
        }

        // Mismo corte que tras leer el perfil: la corrección de arriba espera
        // al servidor y la sesión pudo cerrarse mientras tanto.
        if (sesionCerrandose() || generacion !== generacionDeSesion()) return;

        if (persistedRole && !storeRoleBefore) {
          useAppStore.getState().setRole(persistedRole as any);
          useAppStore.getState().setBaseRole(persistedBaseRole as any);
        } else if (persistedRole && storeRoleBefore && storeRoleBefore !== persistedRole) {
          // El rol guardado manda sobre el que quedó en memoria de una sesión
          // vieja. Sin esto, un rol incorrecto sobrevivía a la corrección.
          useAppStore.getState().setRole(persistedRole as any);
          useAppStore.getState().setBaseRole(persistedBaseRole as any);
        }

        // FIX RESTAURACIÓN PERFIL — Cada cold-start, traemos del Firestore
        // el nombre real de la cédula (OCR), la selfie biométrica como
        // foto de perfil, el teléfono y email guardados. Sin esto, al
        // reinstalar la app el user veía "santo" (prefijo del email) en
        // vez de "Santo De La Rosa" de su cédula, y la foto del perfil
        // quedaba vacía aunque la selfie estuviera ya en /users/{uid}.
        // El check `!storeBefore` evita pisar ediciones que el user
        // haya hecho recientemente y aún no se sincronizaron al Firestore.
        try {
          const storeNow = useAppStore.getState();
          // ══════════════════════════════════════════════════════════════════
          // EL NOMBRE DEL DOCUMENTO NO LLEGABA NUNCA AL PERFIL
          // ══════════════════════════════════════════════════════════════════
          // REPORTADO EN CAMPO (2-oct-2026): «el nombre tampoco se está
          // agregando automático al verificar los documentos; incluso lo
          // escribí manual en el panel y como quiera salió sin nombre, dice
          // Usuario Nuevo».
          //
          // LA CAUSA, MEDIDA: esto leía `displayName`, `first_name` y
          // `last_name`. Pero `reviewKycSubmission` (functions/src/kyc.ts:551)
          // escribe el nombre legal en **`name`**, que no se miraba en ninguna
          // de las tres. Resultado: `profileFullName` salía vacío, no se
          // llamaba a `setProfile`, el store se quedaba en blanco y el perfil
          // pintaba su respaldo: «Usuario Nuevo».
          //
          // `name` va PRIMERO porque es el que escribe el servidor al aprobar;
          // los otros tres se quedan como respaldo para las cuentas viejas.
          const profileFirst = String((profile as any)?.first_name ?? '').trim();
          const profileLast = String((profile as any)?.last_name ?? '').trim();
          const profileDisplay = String((profile as any)?.displayName ?? '').trim();
          const profileName = String((profile as any)?.name ?? '').trim();
          const profileFullName =
            profileName || profileDisplay || `${profileFirst} ${profileLast}`.trim();
          const storeNameBefore = String(storeNow.userName ?? '').trim();

          // ── Y EL NOMBRE VERIFICADO PISA AL QUE HUBIERA ────────────────────
          // La condición era `if (nombre && !storeNameBefore)`: solo rellenaba
          // cuando el store estaba VACÍO. Eso tumbaba justo la regla que pidió
          // el fundador —«si no pusieron el nombre real, automáticamente se
          // coloca el del documento»—, porque quien se registró con un apodo
          // SÍ tiene algo en el store, y entonces el nombre legal no entraba
          // nunca. `nombreVerificado` lo pone el servidor al aprobar: cuando
          // está, el documento manda sobre lo que el teléfono tenga guardado.
          const nombreVerificado = (profile as any)?.nombreVerificado === true;
          const hayQuePonerlo =
            !!profileFullName &&
            (!storeNameBefore || (nombreVerificado && profileFullName !== storeNameBefore));
          if (hayQuePonerlo) {
            const phone = String((profile as any)?.phone ?? storeNow.userPhone ?? '').trim();
            const email = String((profile as any)?.email ?? storeNow.userEmail ?? '').trim();
            storeNow.setProfile(profileFullName, phone, email);
          }
          // ── QUÉ FOTO MANDA ────────────────────────────────────────────────
          //
          // Prioridad: 1) `userPhoto` del documento (la SELFIE verificada — la
          // escribe reviewKycSubmission al aprobar, o kyc-biometrics al
          // subirla), 2) `profilePhoto` (foto de perfil manual), 3) lo que ya
          // hubiera en el store.
          //
          // Antes `profilePhoto` solo se adoptaba si el store estaba vacío, y
          // la selfie del documento NI SE MIRABA: si la cuenta entró con
          // Google, el avatar del correo (un logo de empresa en el caso real
          // del fundador) se quedaba para siempre como la "cara" del chofer.
          // El pasajero decide si montarse mirando esa foto — tiene que ser la
          // selfie verificada, no el logo del correo.
          const selfieVerificada = String((profile as any)?.userPhoto ?? '').trim();
          const profilePhoto = String((profile as any)?.profilePhoto ?? '').trim();
          if (selfieVerificada && selfieVerificada !== storeNow.userPhoto) {
            storeNow.setUserPhoto(selfieVerificada);
            storeNow.setProfilePhotoTaken(true);
          } else if (profilePhoto && !storeNow.userPhoto) {
            storeNow.setUserPhoto(profilePhoto);
            storeNow.setProfilePhotoTaken(true);
          }
          // Restaurar datos del vehículo del chofer — sin esto, después de
          // reinstall/login el RideOfferCard queda sin marca/color/placa
          // aunque Firestore SÍ los tenga desde el KYC.
          const p = profile as any;
          if (p?.vehicleBrand || p?.vehicleModel || p?.vehicleColor || p?.licensePlate) {
            storeNow.setVehicleInfo({
              brand: p.vehicleBrand ?? null,
              model: p.vehicleModel ?? null,
              color: p.vehicleColor ?? null,
              year: p.vehicleYear ?? null,
              plate: p.licensePlate ?? null,
            });
          }
        } catch (e) {
          console.warn('[_layout] restore profile data falló:', (e as any)?.message);
        }
        const currentRole = useAppStore.getState().userRole;
        const isDriverRole =
          currentRole === 'DRIVER' ||
          currentRole === 'SCHOOL_DRIVER' ||
          currentRole === 'DELIVERY_DRIVER';

        // EXCEPCIÓN — el que vino a RE-ELEGIR rol se queda donde está.
        // "Cambiar de rol" limpia userRole/baseRole en el store. Si aquí
        // enrutáramos igual, el usuario que quiere dejar de ser chofer no
        // llegaría nunca a ver el selector. Cuando NO hay rol activo en el
        // store NI en Firestore Y está parado en el picker (/, /role-selection),
        // lo dejamos en paz. Si SÍ tiene rol en Firestore (caso reinstalación)
        // ya lo restauramos arriba, así que esta excepción no aplica.
        // A /role-selection NO se llega por accidente: se llega tocando
        // "Cambiar de rol". Sacarlo de ahí —al panel de su rol viejo o al
        // embudo de KYC— es impedirle exactamente lo que vino a hacer, y era
        // otra jaula: el que eligió "Conductor" por error no podía volver a
        // ser pasajero. Ahí no se decide por él.
        if (currentPath === '/role-selection') {
          return;
        }
        // En "/" (la puerta de entrada) sí re-enrutamos, salvo que no haya rol
        // ni en el store ni en Firestore: entonces la portada es su sitio.
        if (!currentRole && !persistedRole && currentPath === '/') {
          return;
        }

        // ─── ¿HAY ALGO QUE PEDIRLE? ───────────────────────────────────────
        // `kycCompleted` es el biométrico genérico (cédula + selfie). Para los
        // roles de chofer (DRIVER / SCHOOL_DRIVER / DELIVERY_DRIVER) eso NO
        // basta: necesitan además `isDriverVerified` (licencia + matrícula +
        // seguro + fotos del vehículo; el papel de buena conducta ya no, salvo
        // ruta escolar — 27-sep-2026).
        //
        // REVISIÓN EN CURSO: con el KYC MANUAL la aprobación la da una persona
        // y puede tardar horas. A quien YA mandó su cédula y su selfie no se le
        // vuelve a enseñar el embudo: no tiene nada nuevo que hacer ahí.
        // `kycStatus` lo escribe `submitKycForReview` con Admin SDK sobre el
        // mismo doc /users/{uid} que ya leímos — cero lecturas extra.
        const revisionEnCurso = String(profile?.kycStatus ?? '') === 'PENDING';
        const puedoDecidir = !perfilIlegible && !revisionEnCurso;
        const faltaKycBasico = puedoDecidir && !kycOk;
        const faltaKycDeChofer = puedoDecidir && isDriverRole && !driverKycOk;

        if (KYC_WALL_ENFORCED && (faltaKycBasico || faltaKycDeChofer)) {
          // MURO DE VERDAD. Hoy está APAGADO (KYC_WALL_ENFORCED = false en
          // constants/featureFlags.ts). Si algún día se enciende, esta es la
          // única rama que encierra a alguien, y es una decisión consciente.
          router.replace('/kyc-welcome');
          return;
        }

        // OJO: el recordatorio es solo por el KYC BÁSICO (cédula + selfie).
        // Al chofer al que solo le faltan los papeles de chofer NO se le
        // enseña este embudo en cada arranque: /kyc-welcome es además el
        // selector de vehículo, así que devolverlo ahí cada vez le pone
        // delante la posibilidad de reescribir su tipo de vehículo sin
        // quererlo. Su recordatorio ya existe y está mejor puesto: el gate de
        // "ponerse en línea" en map.tsx, que le dice exactamente qué le falta.
        if (faltaKycBasico && !recordatorioKycYaResuelto) {
          // RECORDATORIO, NO MURO — una sola vez por sesión, y /kyc-welcome
          // trae el botón "Ahora no" para entrar igual.
          //
          // Marcamos ANTES de navegar: si no, tocar "Cambiar de rol" lleva a
          // /role-selection (que es ruta de entrada), este guardián vuelve a
          // correr y lo devuelve al embudo. Ese ida y vuelta era una jaula sin
          // barrotes visibles.
          recordatorioKycYaResuelto = true;
          router.replace('/kyc-welcome');
          return;
        }

        // Nada pendiente, o ya lo aplazó: cada quien a su pantalla.
        router.replace(rutaSegunRol(currentRole) as any);
      } catch (e) {
        if (!alive) return;
        // NO se enruta al embudo de KYC. Que falle la lectura del perfil no
        // dice NADA sobre la verificación del usuario: mandarlo ahí era
        // encerrar por mala señal a quien quizá ya está verificado. Se queda
        // donde está y el servidor sigue siendo quien autoriza cada operación.
        console.warn('[_layout] no se pudo leer el perfil; no se enruta:', (e as any)?.message);
      }
    };
    // ── POR QUE ESTA PROMESA NECESITA DUEÑO ────────────────────────────────
    // `run()` iba pelado, y su `try` no empieza hasta la lectura del perfil:
    // todo lo de arriba —leer la sesión del puente y el salto a
    // /verify-email— corría SIN protección. Si algo ahí lanzaba, quedaba una
    // promesa rechazada que nadie atrapa: el ErrorBoundary solo envuelve el
    // dibujado, así que en una compilación de release eso se pierde en
    // silencio y el usuario se queda mirando una pantalla que no avanza.
    run().catch((e) => console.warn('[_layout] el guardián de rutas falló:', (e as any)?.message));
    return () => { alive = false; };
  }, [pathname, userFirebaseUidFromStore, router, setVerified, setDriverVerified]);

  useEffect(() => {
    if (!hasHydrated) return;

    // 1) BLINDAJE WEB EXCLUSIVO PARA OWNER
    // En web producción, SOLO las rutas del panel Rent Car (más /partner) son
    // accesibles. Cualquier intento de visitar /food, /(tabs)/map, /students,
    // /school-*, /kyc-*, /cart, etc. desde el navegador público se redirige
    // a /partner. La app móvil sigue teniendo acceso total a todas las rutas.
    if (Platform.OS === 'web' && !isLocalhostHost()) {
      if (!isRoutePermittedInWebProd(pathname)) {
        router.replace('/partner');
        return;
      }
    }

    // 2) GUARDIA GLOBAL DE AISLAMIENTO POR ROL (Pilar 1, app móvil principalmente).
    // No tocamos rutas comunes (auth, kyc-*, profile, cart, etc.) porque la app
    // debe permanecer abierta hasta que se active el login al final del proyecto.
    if (pathname === '/school-director-dashboard' && userRole !== 'SCHOOL_DIRECTOR') {
      router.replace('/');
      return;
    }
    if (
      pathname === '/school-driver-directory' &&
      userRole !== 'SCHOOL_DIRECTOR' &&
      userRole !== 'PASSENGER'
    ) {
      router.replace('/');
      return;
    }
    if (pathname === '/restaurant-dashboard' && userRole !== 'RESTAURANT_OWNER') {
      router.replace('/');
      return;
    }
    if (pathname === '/crm-settings' && userRole !== 'RENT_CAR_OWNER') {
      router.replace('/');
      return;
    }
    // /admin-panel — NO se filtra por rol de negocio. Antes exigía
    // RENT_CAR_OWNER y expulsaba al administrador real (que puede tener
    // cualquier rol), dejando la página en blanco. La autorización correcta es
    // el CLAIM de admin, y la valida la propia pantalla (admin-panel.tsx)
    // además de las reglas de Firestore, que son la barrera de verdad.

    // 3) LOGIN ENFORCED (feature flag). Redirige a /auth a cualquier user
    //    sin sesión, EXCEPTO en rutas públicas. Incluye AHORA todo el
    //    embudo KYC + verify-email — antes faltaban, y eso disparaba un
    //    bucle infinito de redirecciones cuando el user estaba en
    //    /kyc-welcome y aún no tenía `userFirebaseUid` seteado en el store
    //    (race condition: signup REST → cache hidratando → store null →
    //    guardián tira a /auth → observer detecta no verificado → tira a
    //    /verify-email → user toca atrás → /kyc-welcome → otra vez bucle).
    if (LOGIN_ENFORCED && Platform.OS !== 'web') {
      const publicPaths = new Set<string>([
        '/auth', '/verify-code', '/verify-email', '/partner',
        '/role-selection', '/privacy', '/terms', '/', '/+not-found',
        '/kyc-welcome', '/kyc-documents', '/kyc-biometrics',
        // '/kyc-director' salió de esta lista el 5-oct-2026: era una ruta
        // alcanzable SIN sesión que escribía `schoolName` en /users, y de ese
        // campo se infería el rol de directora en el arranque siguiente.
        '/kyc-subscription',
        '/profile-photo-capture',
      ]);
      const isPublic = pathname && (publicPaths.has(pathname) || pathname.startsWith('/onboard'));
      // Sesión = store O bridge. El bridge cubre el caso REST (cuando el
      // signup cayó al fallback REST, native nunca tuvo currentUser y por
      // eso el observer nunca pudo setear `userFirebaseUid` en el store).
      const storeUid = useAppStore.getState().userFirebaseUid;
      const bridgeUid = getCurrentBridgeUser()?.uid;
      const hasSession = !!(storeUid || bridgeUid);
      if (!isPublic && !hasSession) {
        router.replace('/auth');
        return;
      }
    }
  }, [pathname, userRole, router, hasHydrated]);

  // Precalentar el cache de Store URLs al arrancar — no bloquea nada.
  useEffect(() => {
    configService.getStoreUrls().catch(() => {});
  }, []);

  // FIX UX — Tap en la notificación persistente debe regresar a la pantalla
  // correcta según el `data.type` que pusimos al postarla. Si la app fue
  // killed por Android (memoria), el tap arranca la app desde cero y
  // expo-router intenta abrir la primera ruta — antes quedaba en blanco
  // porque no había `getLastNotificationResponseAsync()` rescuing el deep
  // link. Ahora cubrimos ambos casos: foreground listener + cold-start hook.
  useEffect(() => {
    let mounted = true;

    // ── QUÉ PUSH DEL VIAJE PUEDE MONTAR SU VIAJE — H59, auditoría 16-sep-2026 ──
    //
    // El router solo conocía 'RIDE_STATUS' (la notificación fija que postea la
    // propia app). Los avisos REALES del servidor (functions/src/dispatch.ts:
    // OFFER_ACCEPTED, RIDE_ACCEPTED, RIDE_ARRIVED…) traen `rideId` y se tiraba:
    // tocar "¡Te aceptaron el viaje!" abría la app donde estaba, sin el viaje.
    //
    // NO TODOS pueden llevar `resumeRideId`. El arranque del mapa (map.tsx,
    // "BOOTSTRAP RESUME") monta el viaje del parámetro según el ROL del store y,
    // si ese viaje ya terminó, BORRA el puntero local del viaje. De ahí salen
    // las listas y los cortes de abajo:
    //  · RIDE_DISPATCH es una solicitud PENDING sugerida a un chofer que aún no
    //    la aceptó: el mapa la montaría como un viaje que ya está conduciendo.
    //  · Los avisos de viaje TERMINADO (completado, cancelado, expirado) no
    //    tienen nada que montar, y pasar su id borraría el puntero del viaje
    //    que la persona tenga en curso AHORA.
    //  · Cada aviso sabe a quién va: si el rol del store no es ese, el mapa
    //    montaría el viaje del lado equivocado (un chofer viéndose de pasajero).
    // Todos, eso sí, llevan al mapa, que es donde vive el viaje.
    const VIAJE_PARA_PASAJERO = new Set([
      'RIDE_OFFER', 'RIDE_ACCEPTED', 'RIDE_ARRIVED', 'RIDE_IN_PROGRESS', 'ROUTE_DEVIATION_REQUEST',
    ]);
    const VIAJE_PARA_CHOFER = new Set(['OFFER_ACCEPTED', 'ROUTE_DEVIATION_RESPONSE']);
    // Pueden ir a cualquiera de los dos lados del viaje.
    const VIAJE_PARA_CUALQUIERA = new Set(['RIDE_STATUS', 'RIDE_MESSAGE']);
    const VIAJE_SIN_MONTAR = new Set([
      'RIDE_DISPATCH', 'RIDE_COMPLETED', 'RIDE_CANCELLED', 'RIDE_EXPIRED', 'RIDE_ABANDONED_TIMEOUT',
    ]);

    // El rol se lee del store, que se hidrata del disco al arrancar: en frío
    // puede no haber llegado todavía. Se espera un poco; si no llega, se va al
    // mapa sin montar nada (lo de siempre), nunca con un rol adivinado.
    const esperarStoreHidratado = (ms: number) =>
      new Promise<boolean>((resolve) => {
        if (useAppStore.getState()._hasHydrated) {
          resolve(true);
          return;
        }
        let resuelto = false;
        let quitar: (() => void) | null = null;
        const reloj = setTimeout(() => {
          if (resuelto) return;
          resuelto = true;
          quitar?.();
          resolve(false);
        }, ms);
        quitar = useAppStore.subscribe((s) => {
          if (resuelto || !s._hasHydrated) return;
          resuelto = true;
          clearTimeout(reloj);
          quitar?.();
          resolve(true);
        });
      });

    const viajeQueSePuedeMontar = async (t: string, rideId: string): Promise<string | null> => {
      // El viaje guardado en el teléfono manda: si hay uno y no es este, el
      // aviso es de un viaje anterior y montarlo borraría el de ahora.
      const sesion = await rideSessionStore.read();
      if (sesion.activeRideId && sesion.activeRideId !== rideId) return null;

      if (!(await esperarStoreHidratado(3000))) return null;
      const rol = useAppStore.getState().userRole;
      if (VIAJE_PARA_PASAJERO.has(t)) return rol === 'PASSENGER' ? rideId : null;
      if (VIAJE_PARA_CHOFER.has(t)) return rol === 'DRIVER' ? rideId : null;
      if (VIAJE_PARA_CUALQUIERA.has(t)) {
        return rol === 'PASSENGER' || rol === 'DRIVER' ? rideId : null;
      }
      return null;
    };

    // EN FRÍO (la app arrancó por el toque) hay que montar la pantalla y se usa
    // `replace`, con el viaje como parámetro. CON LA APP VIVA NO: si la persona
    // está en una pantalla del stack raíz (chat, perfil, KYC…), `replace` cambia
    // esa pantalla por una SEGUNDA copia de las pestañas encima de la primera,
    // con otro mapa y otros listeners del mismo viaje. Con la app viva el mapa
    // de abajo ya sigue el viaje: basta volver a él (`dismissTo` cierra lo que
    // haya encima, o reemplaza si no hay pestañas debajo) y sin parámetro, que
    // relanzaría el arranque del viaje sobre uno que ya está montado.
    //
    // V52 — pero `dismissTo` solo sirve con una pantalla del stack raíz ENCIMA
    // de las pestañas. Si la persona ya está en otra pestaña (Perfil,
    // Actividad), expo-router dirige el POP_TO al navegador de pestañas
    // (findDivergentState), TabRouter no lo maneja y useOnAction lo da por
    // hecho sin cambiar nada: la app se quedaba en Perfil. Ahí va `navigate`,
    // que el TabRouter sí maneja (cambia de pestaña, no apila nada). Encima
    // del stack sigue `dismissTo`: el StackRouter vuelve a la (tabs) de abajo
    // y los parámetros nuevos ({screen}) hacen que las pestañas cambien solas.
    // `navigate` desde ahí apilaría una segunda (tabs).
    const rutaEnfocada = (): { enPestanas: boolean; nombre: string | null; params: any } => {
      let enPestanas = false;
      let nombre: string | null = null;
      let params: any = null;
      try {
        let estado: any = navRef.isReady() ? navRef.getRootState() : null;
        while (estado?.routes?.length) {
          const r = estado.routes[estado.index ?? 0];
          if (!r) break;
          if (r.name === '(tabs)') enPestanas = true;
          nombre = r.name;
          params = r.params;
          estado = r.state;
        }
      } catch {}
      return { enPestanas, nombre, params };
    };

    const irA = (href: string, enFrio: boolean) => {
      if (enFrio) router.replace(href as any);
      else if (rutaEnfocada().enPestanas) router.navigate(href as any);
      else router.dismissTo(href as any);
    };

    // V18 — pantallas del stack raíz (panel del restaurante, entrega, chat del
    // pedido, billetera). En frío se monta primero la pantalla base del rol
    // para que Atrás no cierre la app; con la app viva se abre encima, salvo
    // que ya esté abierta esa misma pantalla del mismo pedido.
    const abrirPantalla = (destino: DestinoDeAviso, enFrio: boolean) => {
      if (destino.pathname.startsWith('/(tabs)/')) {
        irA(destino.pathname, enFrio);
        return;
      }
      const href = { pathname: destino.pathname, params: destino.params ?? {} } as any;
      if (enFrio) {
        const base = rutaSegunRol(useAppStore.getState().userRole);
        if (base === destino.pathname) {
          router.replace(href);
          return;
        }
        router.replace(base as any);
        router.push(href);
        return;
      }
      const actual = rutaEnfocada();
      if (
        actual.nombre === destino.pathname.slice(1) &&
        String(actual.params?.orderId ?? '') === String(destino.params?.orderId ?? '')
      ) {
        return;
      }
      router.push(href);
    };

    // V54 — los tipos que el servidor manda de verdad (functions/src/loyalty.ts
    // e incentives.ts). 'LOYALTY', 'LOYALTY_REWARD' y 'DRIVER_INCENTIVE' no los
    // manda nadie.
    const AVISOS_DE_INCENTIVO = new Set([
      'MONTHLY_GOAL_PAID', 'MONTHLY_GOAL_NUDGE', 'LIFETIME_MILESTONE_PAID',
      'REFERRAL_SIGNED_UP', 'REFERRAL_PROGRESS', 'REFERRAL_BONUS_PAID',
    ]);

    const routeFromData = async (data: any, enFrio: boolean) => {
      if (!data || typeof data !== 'object') return;
      const t = String(data.type ?? '');
      // El SOS y los casos de fraude los atiende el admin desde el panel web.
      if (AVISOS_SIN_NAVEGACION.has(t)) return;
      const esDelViaje =
        VIAJE_PARA_PASAJERO.has(t) ||
        VIAJE_PARA_CHOFER.has(t) ||
        VIAJE_PARA_CUALQUIERA.has(t) ||
        VIAJE_SIN_MONTAR.has(t);
      if (esDelViaje && !enFrio) {
        // Un mensaje del chat con la app abierta no saca a nadie de donde está
        // (puede estar ya dentro del chat). El resto vuelve al mapa.
        if (t === 'RIDE_MESSAGE') return;
        irA('/(tabs)/map', false);
        return;
      }
      if (esDelViaje) {
        // FIX RESTAURACIÓN DEL RIDE — Antes navegábamos genéricamente a
        // /(tabs)/map y el usuario aterrizaba en la pantalla de "Solicitar
        // Viaje" aunque tuviera un ride activo. Ahora pasamos rideId como
        // query param; map.tsx lo lee al montar y re-arma el estado del
        // viaje (currentRideId, isActiveTrip, listeners de Firestore).
        const rideId = data.rideId ? String(data.rideId) : null;
        let montar: string | null = null;
        if (rideId && !VIAJE_SIN_MONTAR.has(t)) {
          try {
            montar = await viajeQueSePuedeMontar(t, rideId);
          } catch {
            montar = null; // ante la duda, al mapa sin montar nada
          }
        }
        if (montar) {
          router.replace({ pathname: '/(tabs)/map', params: { resumeRideId: montar } } as any);
        } else {
          router.replace('/(tabs)/map');
        }
      } else if (
        t === 'CRM_CANDIDATE_APPROVED' ||
        t === 'CRM_CANDIDATE_REJECTED' ||
        t === 'CRM_VEHICLE_RELEASED'
      ) {
        // Los avisos del candidato de rent car. Sin esta rama caían al final
        // del enrutador sin hacer nada: el aviso decía «te aprobaron» y tocarlo
        // no llevaba a ninguna parte — el patrón por el que esta cuenta ya fue
        // rechazada una vez en Play («elementos de UI que no responden»).
        irA('/mi-postulacion', false);
      } else if (t === 'PROMO') {
        irA('/(tabs)/food', enFrio);
      } else if (t.startsWith('LOYALTY_')) {
        irA('/(tabs)/map', enFrio);
      } else if (AVISOS_DE_INCENTIVO.has(t)) {
        irA('/(tabs)/map', enFrio);
      } else if (t === 'PENALTY_BLOCKED' || t === 'PENALTY_WARNING') {
        // La multa y el botón para pagarla viven en el PenaltyGate del mapa; la
        // billetera no la muestra y taparía ese aviso.
        irA('/(tabs)/map', enFrio);
      } else if (
        t === 'CREDITO_EN_REVISION' ||
        t === 'DRIVER_TRIAL_EXPIRED' ||
        t === 'PAYMENT_BLOCK_CLEARED' ||
        t === 'MEMBERSHIP_PAYMENT_DUE' ||
        t === 'MONTHLY_CYCLE' ||
        t === 'WALLET_ADJUSTED' ||
        t === 'CANCELLATION_COMPENSATION' ||
        t === 'PENALTY_CLEARED' ||
        // La cuota pagada con tarjeta (pagoCuotaStripe.ts): "Pago recibido".
        t === 'CUOTA_PAGADA' ||
        t.startsWith('RECHARGE_')
      ) {
        // Saldo a favor, cuota, recargas, multas y bloqueos se ven en la
        // billetera: el mismo destino que ya les da la campana.
        abrirPantalla({ pathname: '/wallet' }, enFrio);
      } else if (t === 'SEGURO_VENCIDO' || t === 'SEGURO_POR_VENCER') {
        // EL AVISO DEL SEGURO LLEVA A DONDE SE ARREGLA.
        // Son los dos unicos avisos que el chofer puede resolver el mismo sin
        // que nadie intervenga primero (`revisarSegurosVencidos`, seguro.ts):
        // toma la foto de su poliza nueva y la manda. Mandarlo al mapa seria
        // decirle «se te vence el seguro» y dejarlo buscando donde.
        abrirPantalla({ pathname: '/seguro' }, enFrio);
      } else if (esAvisoDeComida(t)) {
        if (enFrio) await esperarStoreHidratado(3000);
        const s = useAppStore.getState();
        const destino = await destinoDeAvisoDeComida(
          t,
          data.orderId ? String(data.orderId) : null,
          s.userFirebaseUid ?? null,
          s.userRole ?? null
        );
        if (destino && mounted) abrirPantalla(destino, enFrio);
      }
    };

    let unsubscribe: (() => void) | null = null;
    let claveManejadaEnFrio: string | null = null;

    (async () => {
      try {
        // Dynamic import: expo-notifications puede no estar instalado en web.
        const Notifications = await import('expo-notifications');

        // C3 FIX — Cold-start reroute persistente. Antes usábamos una var
        // local `ColdStartChecked` que se reiniciaba en cada re-mount del
        // effect (deps `[router]`) → cada re-render enrutaba a un ride viejo,
        // incluso a un ride COMPLETED. Ahora persistimos el `notificationId`
        // ya procesado en AsyncStorage y solo enrutamos si el actual es NUEVO.
        const LAST_HANDLED_KEY = 'gs_last_handled_notification_id';

        // H13, auditoría 16-sep-2026 — SE RECUERDA EL TOQUE, NO LA NOTIFICACIÓN.
        //
        // Se guardaba solo `request.identifier`. Pero la notificación fija del
        // viaje usa SIEMPRE el mismo identificador, 'gs-ride-status', y es a
        // propósito: así Android la reemplaza en vez de apilar una por estado
        // (notificationService.ts). Resultado: el primer arranque tocándola
        // grababa 'gs-ride-status' como "ya manejada" y desde ahí TODO toque en
        // frío sobre la notificación de cualquier viaje futuro se descartaba
        // como repetido, y su rideId se perdía.
        //
        // La fecha de la notificación cambia cada vez que se postea (cada
        // cambio de estado del viaje), así que identificador+fecha separa un
        // aviso de otro y sigue frenando lo que C3 frenaba: el MISMO aviso
        // re-entregado al volver a montar o al recrear la actividad. Los push
        // del servidor ya traían un identificador único; con la fecha siguen
        // siéndolo.
        const claveDelToque = (resp: any): string | null => {
          const id = resp?.notification?.request?.identifier;
          if (!id) return null;
          const fecha = resp?.notification?.date;
          return `${id}:${typeof fecha === 'number' || typeof fecha === 'string' ? fecha : ''}`;
        };

        const last = await Notifications.getLastNotificationResponseAsync?.();
        const lastKey = claveDelToque(last);
        if (lastKey && last?.notification?.request?.content?.data && mounted) {
          try {
            const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
            const prevHandled = await AsyncStorage.getItem(LAST_HANDLED_KEY);
            if (prevHandled !== lastKey) {
              claveManejadaEnFrio = lastKey;
              await AsyncStorage.setItem(LAST_HANDLED_KEY, lastKey);
              setTimeout(() => {
                routeFromData(last!.notification.request.content.data, true).catch(() => {});
              }, 200);
            }
          } catch {
            // Sin AsyncStorage: mejor NO enrutar (evita loops) que enrutar mal.
          }
        }

        // 2) Runtime: foreground o background → cuando el user toca la notif.
        const sub = Notifications.addNotificationResponseReceivedListener?.((response: any) => {
          if (!mounted) return;
          // También marcamos el toque como manejado para consistencia con cold-start.
          const clave = claveDelToque(response);
          // En algunos Android el toque que ARRANCÓ la app llega también por
          // aquí: ya lo enrutó el arranque en frío, no se enruta dos veces.
          if (clave && clave === claveManejadaEnFrio) return;
          if (clave) {
            import('@react-native-async-storage/async-storage')
              .then((mod) => mod.default.setItem(LAST_HANDLED_KEY, clave))
              .catch(() => {});
          }
          routeFromData(response?.notification?.request?.content?.data, false).catch(() => {});
        });
        unsubscribe = () => sub?.remove?.();
      } catch (e) {
        console.warn('[_layout] notification tap handler init falló:', e);
      }
    })();

    return () => {
      mounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [router]);

  // ══════════════════════════════════════════════════════════════════════════
  // BOTÓN "ATRÁS" DE ANDROID — la app se CERRABA de golpe.
  //
  // Causa verificada: expo-router registra un único listener de 'hardwareBackPress'
  // (node_modules/expo-router/build/fork/useBackButton.native.js). Si
  // canGoBack() es false, devuelve el evento a Android → Android hace finish()
  // de la Activity → la app DESAPARECE. No es un error: no hay diálogo, no hay
  // mensaje, simplemente se cierra.
  //
  // Y canGoBack() es false en casi todas las pantallas de aterrizaje porque
  // todo el embudo navega con router.replace (151 usos en el proyecto), que
  // destruye la entrada anterior de la pila. Al abrir la app y caer en el mapa,
  // la pila tiene UNA sola entrada: un toque de Atrás y se acabó.
  //
  // En un teléfono con navegación por gestos (Android 10+), el deslizamiento
  // desde el borde ES ese mismo botón: basta rozar la pantalla sin querer.
  //
  // Solución: el patrón que usan Uber e InDriver — el primer Atrás avisa, el
  // segundo (dentro de 2 s) sí sale. Va por OTA, no necesita recompilar.
  // ══════════════════════════════════════════════════════════════════════════
  // Atrapa lo que el ErrorBoundary no puede: promesas rechazadas sin catch.
  useEffect(() => { instalarManejadorGlobal(); }, []);

  // ══ CONTEXTO DE LOS INFORMES DE FALLO ═══════════════════════════
  // Un informe sin contexto dice "algo falló" y ya. Con esto, en la consola se
  // lee "pasajero, en /(tabs)/map" — que es la diferencia entre saber dónde
  // mirar y volver a perder dos días.
  //
  // Se manda el ROL y la RUTA, nunca datos personales: el identificador de
  // usuario sirve para agrupar y para saber a cuánta gente afecta, y no lleva
  // teléfono, correo ni nombre.
  const uidParaInformes = useAppStore((state) => state.userFirebaseUid);
  useEffect(() => {
    try {
      errorReporter.setUser(uidParaInformes ?? null, { role: userRole ?? undefined });
    } catch { /* nunca romper el arranque por un informe */ }
  }, [uidParaInformes, userRole]);

  useEffect(() => {
    try {
      if (pathname) errorReporter.setPantalla(pathname);
    } catch { /* idem */ }
  }, [pathname]);

  const ultimoAtrasRef = useRef(0);
  useEffect(() => {
    if (Platform.OS !== 'android') return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      // Si hay a dónde volver, que expo-router haga lo suyo.
      if (globalRouter.canGoBack()) return false;
      const ahora = Date.now();
      if (ahora - ultimoAtrasRef.current < 2000) return false; // segundo toque: salir
      ultimoAtrasRef.current = ahora;
      try { ToastAndroid.show('Toca Atrás otra vez para salir', ToastAndroid.SHORT); } catch {}
      return true; // consumimos el evento: la app NO se cierra
    });
    return () => sub.remove();
  }, []);

  // ONBOARDING DE PRIMERA VEZ — solo en móvil, solo si nunca lo vio.
  // No corre en web (el panel owner no necesita tutorial).
  useEffect(() => {
    if (!hasHydrated) return;
    if (Platform.OS === 'web') return;
    if (pathname === '/onboarding') return; // ya está dentro
    // Importación dinámica para evitar load eager innecesario
    import('./onboarding').then(({ isOnboardingDone }) => {
      isOnboardingDone().then((done) => {
        if (!done && pathname === '/') {
          router.replace('/onboarding' as any);
        }
      });
    });
  }, [hasHydrated, pathname, router]);

  // FIX CRÍTICO WEB EN BLANCO — Antes esto era `if (!loaded && !error) return null`.
  // En el export web `useFonts` puede quedarse SIN resolver (loaded=false y
  // error=null para siempre) y la app NO pintaba NADA: TODA la web salía en
  // blanco tras hidratar (verificado en producción: rootChildren=0 en /partner,
  // /admin-panel, etc.). La fuente de íconos es COSMÉTICA y en web ya se
  // inyecta por CSS más arriba, así que jamás debe bloquear el arranque.
  // Ahora, pasados 2 s (ESPERA_FUENTES_MS), la app arranca igual y los íconos
  // caen al fallback del sistema.
  //
  // Esta condición tiene que ser IDÉNTICA a la del efecto que esconde el
  // splash. Mientras no lo fueron, el plazo destapaba el dibujado pero el
  // splash se quedaba encima para siempre: la app viva y nadie viéndola.
  if (!loaded && !error && !fontTimeout) {
    return null;
  }

  const useOwnerShell = shouldUseOwnerShell(pathname, userRole);

  const stack = (
    <Stack
      screenOptions={{
        headerShown: false,
        contentStyle: { backgroundColor: Colors.background },
        // Transición suave entre pantallas (slide horizontal en iOS, fade en web)
        animation: Platform.OS === 'web' ? 'fade' : 'slide_from_right',
        animationDuration: 220,
        gestureEnabled: true,
        fullScreenGestureEnabled: true,
      }}
    >
      <Stack.Screen name="index" />
      <Stack.Screen name="auth" />
      <Stack.Screen name="verify-email" />
      <Stack.Screen name="partner" />
      <Stack.Screen name="(tabs)" />
      <Stack.Screen name="school-director-dashboard" />
      <Stack.Screen name="school-driver-directory" />
    </Stack>
  );

  return (
    // GestureHandlerRootView es OBLIGATORIO para @gorhom/bottom-sheet (Uber-style)
    // y para gestos de swipe entre pantallas. Debe envolver TODO el árbol.
    <GestureHandlerRootView style={{ flex: 1 }}>
      {/* RED DE SEGURIDAD — sin esto, cualquier excepción de dibujado cerraba
          el APK en seco, sin mensaje. Ahora muestra una pantalla con "Volver a
          intentar" y la app sigue viva. Ver components/ErrorBoundary.tsx. */}
      <ErrorBoundary>
      {/* FIX AUDIT #4: NO fallback a pk_test_TYooMQauvdEDq54NiTphI7jx
          (clave sandbox Stripe). Antes en release, si EXPO_PUBLIC_STRIPE_KEY
          faltaba en .env → app usaba sandbox y cobros iban a una cuenta de
          prueba. Ahora string vacío → Stripe SDK falla loud en lugar de
          procesar pagos silenciosamente al ambiente equivocado. */}
      <StripeProviderWrapper publishableKey={process.env.EXPO_PUBLIC_STRIPE_KEY || ""}>
        <View style={styles.rootContainer}>
          {useOwnerShell ? <DesktopSidebarLayout>{stack}</DesktopSidebarLayout> : stack}
          {/* Banner global "Sin conexión" → solo aparece cuando el dispositivo
              pierde internet. Auto-mensaje "Conexión restaurada" cuando regresa. */}
          <OfflineBanner />
          <AvisoDeActualizacion />
        </View>
      </StripeProviderWrapper>
      </ErrorBoundary>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  rootContainer: {
    flex: 1,
    width: '100%',
    backgroundColor: Colors.background,
  }
});
